import type { ReactNode } from 'react';
import type { UserAvatarUser } from '@/components/UserAvatar';
import { Check, ExternalLink } from 'lucide-react';
import { FOCUS_RING } from '@/components/console-ui';
import { UserAvatar } from '@/components/UserAvatar';
import { cn } from 'helpers/cn';
import { LogoBars } from './LogoBars';

/** Who the assistant's messages are from, next to the bars (decided 2026-10-17: "Pertento", not "Pertento AI"). */
export const AI_AUTHOR = 'Pertento';

/** The bars' motion while a message is being worked on; at rest they stand still. */
export type ChatWorkingPhase = 'thinking' | 'writing';

type AiMessageProps = {
  children: ReactNode;
  barsState?: 'idle' | ChatWorkingPhase;
  footnote?: string;
  /** "asked by Anna": in a shared thread, an answer says whose question it answers. */
  askedBy?: string;
};

/** An AI message: the author row ("Pertento · asked by Anna"), then the body. No bubble. */
export const AiMessage = ({ children, barsState = 'idle', footnote, askedBy }: AiMessageProps) => (
  <div className="flex flex-col gap-1.5">
    <p data-ai-author="" className="flex min-w-0 items-center gap-2 text-micro font-semibold text-foreground">
      <LogoBars size={12} state={barsState === 'idle' ? 'still' : barsState} />
      {AI_AUTHOR}
      {askedBy && (
        <span data-ai-asked-by="" className="-ml-1 min-w-0 truncate font-normal text-dim">
          · {askedBy}
        </span>
      )}
    </p>
    <div className="text-meta leading-relaxed text-foreground">{children}</div>
    {footnote && <p className="font-mono text-nano text-dim">{footnote}</p>}
  </div>
);

type SenderRowProps = { user: UserAvatarUser; name: string };

/**
 * Who sent the message below, in a shared thread: their badge (photo or initials, as everywhere
 * else) and first name, right-aligned with the message.
 */
export const SenderRow = ({ user, name }: SenderRowProps) => (
  <p data-ai-sender="" className="-mb-3 flex items-center justify-end gap-1.5 text-micro font-medium text-dim">
    <UserAvatar user={user} size="xs" />
    <span className="truncate">{name}</span>
  </p>
);

/** The person's message: a bubble on the right, their text as typed. */
export const UserMessage = ({ text }: { text: string }) => (
  <div className="flex justify-end">
    <UserBubble>{text}</UserBubble>
  </div>
);

/** The bubble itself, for a message that carries more than plain text (the editor's answer to a question). */
export const UserBubble = ({ children }: { children: ReactNode }) => (
  <p className="max-w-[86%] rounded-card rounded-br-sm bg-field px-3 py-2.5 text-meta leading-relaxed whitespace-pre-wrap text-foreground">
    {children}
  </p>
);

type WorkingStepsProps = {
  steps: readonly string[];
  phase: ChatWorkingPhase;
  /** Shown beside the current step, e.g. the running credit count. */
  live?: string;
};

/**
 * A turn while it runs: the steps as the server names them, done ones ticked and muted, the
 * current one with thinking or writing bars.
 */
export const WorkingSteps = ({ steps, phase, live }: WorkingStepsProps) => (
  <ul className="space-y-1.5" aria-label="Progress">
    {steps.map((step, index) => {
      const current = index === steps.length - 1;
      return (
        <li
          key={`${index}:${step}`}
          className={cn('flex items-center gap-2', current ? 'font-medium text-foreground' : 'text-dim')}
        >
          {current ? <LogoBars size={11} state={phase} /> : <Check aria-hidden="true" className="size-3.5 text-ok" />}
          <span>{current ? `${step}…` : step}</span>
          {current && live && (
            <span data-ai-live-credits="" className="font-mono text-nano font-normal text-dim">
              · {live}
            </span>
          )}
        </li>
      );
    })}
  </ul>
);

/** A collapsed earlier turn: the still bars, a one-line receipt and its own control. */
export const TurnReceipt = ({ receipt, children }: { receipt: string; children: ReactNode }) => (
  <div className="flex items-center gap-2 text-micro text-dim">
    <LogoBars size={11} state="still" />
    <span className="min-w-0 flex-1 truncate">{receipt}</span>
    {children}
  </div>
);

type TextButtonProps = { children: ReactNode; onClick: () => void; className?: string };

export const TextButton = ({ children, onClick, className }: TextButtonProps) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      'inline-flex items-center gap-1 self-start rounded-sm text-micro font-medium text-accent hover:underline',
      FOCUS_RING,
      className,
    )}
  >
    {children}
  </button>
);

type ExternalTextLinkProps = { href: string; children: ReactNode };

export const ExternalTextLink = ({ href, children }: ExternalTextLinkProps) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={cn('inline-flex items-center gap-0.5 font-medium text-accent hover:underline', FOCUS_RING)}
  >
    {children}
    <ExternalLink aria-hidden="true" className="size-3" />
  </a>
);
